// Customers API
import api, { type AxiosResponse } from './client';
import type {
  CustomerProfile,
  CustomerDashboardStats,
  CustomerPerformanceMetric,
  CustomerCertification,
  CustomerMessage,
  CreditScore,
  QueryParams,
} from './types';

export const customersAPI = {
  // Customer Profiles
  getProfiles: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CustomerProfile[] }>> =>
    api.get('/customers/profiles/', { params }),

  getProfile: (id: number, params?: QueryParams): Promise<AxiosResponse<CustomerProfile>> =>
    api.get(`/customers/profiles/${id}/`, { params }),

  updateProfile: (id: number, data: Partial<CustomerProfile>, params?: QueryParams): Promise<AxiosResponse<CustomerProfile>> =>
    api.patch(`/customers/profiles/${id}/`, data, { params }),

  getDashboardStats: (params?: QueryParams): Promise<AxiosResponse<CustomerDashboardStats>> =>
    api.get('/customers/profiles/dashboard_stats/', { params }),

  getTopRated: (): Promise<AxiosResponse<CustomerProfile[]>> =>
    api.get('/customers/profiles/top_rated/'),

  // Performance Metrics
  getMetrics: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CustomerPerformanceMetric[] }>> =>
    api.get('/customers/metrics/', { params }),

  // Certifications
  getCertifications: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CustomerCertification[] }>> =>
    api.get('/customers/certifications/', { params }),

  // CR-3375: Customer OTIF Settings
  getOtifSettings: (profileId: number, params?: QueryParams): Promise<AxiosResponse<{
    customer_id: number;
    customer_name: string;
    use_custom_otif_window: boolean;
    otif_days_early_allowed: number;
    otif_days_late_allowed: number;
  }>> =>
    api.get(`/customers/profiles/${profileId}/otif-settings/`, { params }),

  updateOtifSettings: (profileId: number, data: {
    use_custom_otif_window?: boolean;
    otif_days_early_allowed?: number;
    otif_days_late_allowed?: number;
  }, params?: QueryParams): Promise<AxiosResponse<{
    status: string;
    message: string;
    data: {
      customer_id: number;
      customer_name: string;
      use_custom_otif_window: boolean;
      otif_days_early_allowed: number;
      otif_days_late_allowed: number;
    };
  }>> =>
    api.patch(`/customers/profiles/${profileId}/otif-settings/`, data, { params }),

  // Stock Levels
  getStockLevels: (params?: {
    customer?: number;
    supplier?: number;
    part_number?: string;
    search?: string;
    ordering?: string;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<{
    count: number;
    results: CustomerStockLevel[];
  }>> =>
    api.get('/customers/stock-levels/', { params }),

  getStockLevel: (id: number): Promise<AxiosResponse<CustomerStockLevel>> =>
    api.get(`/customers/stock-levels/${id}/`),

  createStockLevel: (data: {
    supplier: number;
    part_number: string;
    quantity_available: number;
    unit_of_measure?: string;
    notes?: string;
  }): Promise<AxiosResponse<CustomerStockLevel>> =>
    api.post('/customers/stock-levels/', data),

  updateStockLevel: (id: number, data: {
    quantity_available?: number;
    unit_of_measure?: string;
    notes?: string;
  }): Promise<AxiosResponse<CustomerStockLevel>> =>
    api.patch(`/customers/stock-levels/${id}/`, data),

  deleteStockLevel: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/customers/stock-levels/${id}/`),

  bulkUploadStockLevels: (file: File): Promise<AxiosResponse<{
    success: boolean;
    created: number;
    updated: number;
    errors: Array<{ row: number; errors: Record<string, string[]>; data: Record<string, string> }>;
    total_errors: number;
  }>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/customers/stock-levels/bulk-upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  downloadStockTemplate: (): Promise<AxiosResponse<Blob>> =>
    api.get('/customers/stock-levels/download-template/', {
      responseType: 'blob',
    }),

  exportStockLevels: (params?: { part_number?: string }): Promise<AxiosResponse<Blob>> =>
    api.get('/customers/stock-levels/export/', {
      params,
      responseType: 'blob',
    }),

  // Create customer from onboarding wizard
  createFromWizard: (data: {
    company_name: string;
    company_address?: string;
    description?: string;
    currency?: string;
    annual_spend?: string;
    logo_url?: string;
    supplier_id: number;
    supplier_ids?: number[];
  }): Promise<AxiosResponse<{
    customer_id: number;
    company_name: string;
    onboarding_status: string;
  }>> =>
    api.post('/customers/profiles/create-from-wizard/', data),

  // Save wizard draft state for resuming onboarding wizard
  saveWizardDraft: (companyId: number, data: {
    form_data: Record<string, unknown>;
    current_step: number;
    highest_step: number;
  }): Promise<AxiosResponse<{ status: string; wizard_draft: Record<string, unknown> }>> =>
    api.patch(`/customers/profiles/${companyId}/save-draft/`, data),

  // CR-4056: Toggle customer active/inactive
  toggleCustomerActive: (id: number, isActive?: boolean, params?: QueryParams): Promise<AxiosResponse<{ status: string; is_active: boolean; message: string }>> =>
    api.post(`/customers/profiles/${id}/toggle-active/`, isActive !== undefined ? { is_active: isActive } : {}, { params }),

  // CR-4161: Credit Scores
  getCreditScores: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CreditScore[] }>> =>
    api.get('/customers/credit-scores/', { params }),

  createCreditScore: (data: FormData): Promise<AxiosResponse<CreditScore>> =>
    api.post('/customers/credit-scores/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateCreditScore: (id: number, data: FormData): Promise<AxiosResponse<CreditScore>> =>
    api.patch(`/customers/credit-scores/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteCreditScore: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/customers/credit-scores/${id}/`),

  // CR-4146: UK Companies House search
  searchCompaniesHouse: (query: string): Promise<AxiosResponse<{
    results: Array<{
      registration_number: string;
      company_name: string;
      address: string;
      incorporated_on: string;
      company_status: string;
      company_type: string;
    }>;
  }>> =>
    api.get('/customers/companies-house/search/', { params: { q: query } }),

  // Performance Matrix - monthly OTIF ratings per customer
  getPerformanceMatrix: (params?: { year?: number }): Promise<AxiosResponse<PerformanceMatrixResponse>> =>
    api.get('/customers/performance-matrix/', { params }),

  // Customer Messages (internal communication)
  getMessages: (params: { customer: number; supplier: number; page_size?: number }): Promise<AxiosResponse<{ count: number; results: CustomerMessage[] }>> =>
    api.get('/customers/messages/', { params }),

  sendMessage: (data: { customer: number; supplier: number; content: string }): Promise<AxiosResponse<CustomerMessage>> =>
    api.post('/customers/messages/', data),
};

export interface PerformanceMatrixRow {
  customer_id: number;
  Customer: string;
  CustomerCode: string;
  Business: string;
  Jan: string; Feb: string; Mar: string; Apr: string; May: string; Jun: string;
  Jul: string; Aug: string; Sep: string; Oct: string; Nov: string; Dec: string;
  Jan_pct: number | null; Feb_pct: number | null; Mar_pct: number | null;
  Apr_pct: number | null; May_pct: number | null; Jun_pct: number | null;
  Jul_pct: number | null; Aug_pct: number | null; Sep_pct: number | null;
  Oct_pct: number | null; Nov_pct: number | null; Dec_pct: number | null;
  OverallRating: string;
  OverallPct: number | null;
  TotalLines: number;
  OtifLines: number;
}

export interface PerformanceMatrixResponse {
  year: number;
  rows: PerformanceMatrixRow[];
  total: number;
}

// Stock Level type
export interface CustomerStockLevel {
  id: number;
  customer: number;
  customer_name: string;
  supplier: number;
  supplier_name: string;
  part_number: string;
  quantity_available: number;
  unit_of_measure: string;
  last_updated: string;
  updated_by: number | null;
  updated_by_name: string | null;
  notes: string;
  created_at: string;
}
